import axios from 'axios'
import type {
  SiteGeoJSON,
  SiteDetail,
  FilterOptions,
  ConflictEventsGeoJSON,
  DamageReport,
  NarrativeCallbacks,
} from '../types'

const api = axios.create({ baseURL: '/api' })

export async function fetchSites(
  filters: {
    country?: string
    period?: string
  } = {},
): Promise<SiteGeoJSON> {
  const params = new URLSearchParams()
  if (filters.country) params.set('country', filters.country)
  if (filters.period) params.set('period', filters.period)
  const { data } = await api.get<SiteGeoJSON>(`/sites?${params}`)
  return data
}

export async function fetchSiteDetail(siteId: string, signal?: AbortSignal): Promise<SiteDetail> {
  const { data } = await api.get<SiteDetail>(`/sites/${encodeURIComponent(siteId)}`, { signal })
  return data
}

export async function fetchFilterOptions(): Promise<FilterOptions> {
  const { data } = await api.get<FilterOptions>('/sites/filters/options')
  return data
}

export async function fetchConflictEvents(params?: {
  from?: string
  to?: string
}): Promise<ConflictEventsGeoJSON> {
  const qs = new URLSearchParams()
  if (params?.from) qs.set('from', params.from)
  if (params?.to) qs.set('to', params.to)
  const { data } = await api.get<ConflictEventsGeoJSON>(`/events?${qs}`)
  return data
}

export async function fetchDamageHistory(siteId: string): Promise<DamageReport[]> {
  const { data } = await api.get<DamageReport[]>(
    `/sites/${encodeURIComponent(siteId)}/damage-history`,
  )
  return data
}

export async function triggerSync(): Promise<unknown> {
  const { data } = await api.post('/sites/sync')
  return data
}

/**
 * Stream a narrative for a site. If cached on the server, resolves
 * immediately via a plain JSON response. Otherwise opens an SSE
 * stream and calls onDelta for each text chunk.
 * Returns an abort function.
 */
export function streamNarrative(
  siteId: string,
  { onDelta, onCached, onDone, onError }: NarrativeCallbacks,
): () => void {
  const controller = new AbortController()

  ;(async () => {
    try {
      const res = await fetch(`/api/narratives/${encodeURIComponent(siteId)}`, {
        signal: controller.signal,
      })

      if (!res.ok) {
        onError?.('Could not generate narrative.')
        return
      }

      const contentType = res.headers.get('content-type') || ''

      // Fast path: cached narrative returned as JSON
      if (contentType.includes('application/json')) {
        const json = await res.json()
        if (json.narrative) {
          onCached?.(json.narrative)
        } else {
          onError?.('Could not generate narrative.')
        }
        onDone?.()
        return
      }

      // Slow path: SSE stream
      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()!

        let currentEvent: string | null = null
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7)
          } else if (line.startsWith('data: ') && currentEvent) {
            const data = line.slice(6)
            if (currentEvent === 'delta') {
              onDelta?.(JSON.parse(data))
            } else if (currentEvent === 'done') {
              onDone?.()
            } else if (currentEvent === 'error') {
              try {
                const parsed = JSON.parse(data)
                onError?.(typeof parsed === 'string' ? parsed : 'Could not generate narrative.')
              } catch {
                onError?.(data)
              }
            }
            currentEvent = null
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      onError?.((err as Error).message)
    }
  })()

  return () => controller.abort()
}
